import map from "lodash/map"
import isEmpty from "lodash/isEmpty"
import get from "lodash/get"
import filter from "lodash/filter"
import assign from "lodash/assign"
import { useEffect, useRef, useState } from "react"
import { ReactSVG } from "react-svg"
import dayjs from "utils-lib/date"
import { makeStyles } from "tss-react/mui"
import Box from "component-lib/Base/components/Box"
import { Typography } from "component-lib/Base/components/Typography/Typography"
import { getWindow } from "utils-lib/getWindow"
import getContentUrl from "utils-lib/getContentUrl"
import { ButtonArrow, Button, Link, FormattedTextField } from "../../../../Base"
import formatUrl from "../utils/formatUrl"
import { pushDropoffLocationFacilityListingSelection } from "../Analytics/analytics"

const _map = map.bind(map)

const ssrWindow = getWindow()

const alertIconSvg = `/content/dam/wm/icons/red-alert-exclamation-circle.svg`

const useStyles = makeStyles()((theme) => ({
  root: {
    [theme.breakpoints.down(`md`)]: {
      width: `100vw`,
      background: get(theme, `palette.common.white`, ``),
      overflowY: `auto`,
      minHeight: "100px",
    },
  },
  leftOffset: {
    paddingLeft: `0.5rem`,
  },
  leftInnerOffset: {
    paddingLeft: `1.75rem`,
    marginRight: `1.5rem`,
    [theme.breakpoints.down(`sm`)]: {
      paddingLeft: `0.75rem`,
    },
  },
  rightOffset: {
    paddingRight: `0.1rem`,
    [theme.breakpoints.down(`sm`)]: {
      paddingRight: 0,
    },
  },
  alertIcon: {
    display: `flex`,
    alignItems: `center`,
    width: `1.25rem`,
    "& svg": {
      height: `1.25rem`,
    },
    marginRight: `.25rem`,
  },
  expandButton: {
    marginRight: `0.5rem`,
    paddingBottom: `0.5rem`,
    "& .react-svg": {
      height: `20px`,
    },
  },
  hoursAndHolidays: {
    padding: 0,
    marginTop: `.75rem`,
    paddingTop: `.5rem`,
  },
  alertBox: {
    display: `flex`,
    background: get(theme, `palette.background.gray`, ``),
    padding: `1rem 2.5rem`,
    marginBottom: `1.5rem`,
  },
  bottomBorder: {
    borderBottom: `1px solid ${get(theme, `palette.border.dividerLight`, ``)}`,
  },
  topBorder: {
    borderTop: `1px solid ${get(theme, `palette.border.dividerLight`, ``)}`,
  },
  errorStyle: {
    color: get(theme, `palette.text.error`, ``),
    marginBottom: `1rem`,
  },
  timeSlotWrapper: {
    display: `flex`,
    justifyContent: `space-between`,
    paddingBottom: `0.5rem`,
    "& .segment": {
      padding: `0 1.5rem`,
      [theme.breakpoints.down(`xs`)]: {
        padding: `0 0.1rem`,
      },
      "& .slot": {
        width: `5.5rem`,
      },
      "& .begin": {
        paddingRight: `0.5rem`,
      },
      "& .end": {
        paddingLeft: `0.5rem`,
      },
    },
    [theme.breakpoints.down(`md`)]: {
      maxWidth: `50%`,

      [theme.breakpoints.down(`sm`)]: {
        maxWidth: `100%`,
      },
    },
  },
}))

export const FacilityDetails = ({
  map,
  onCloseFacility,
  facilityAlerts,
  siteLanguage,
  strings,
  style = {},
}) => {
  const { classes } = useStyles()
  const [isHoursOpen, setIsHoursOpen] = useState(false)
  const rerender = useRef(false)

  useEffect(() => {
    let facilityId = ""
    if (facilityId !== map?.selectedFacilityId) {
      rerender.current = false
    }

    if (!rerender.current) {
      const facilities = get(map, `mapLocations`, []).filter(
        (loc) =>
          get(loc, `properties.uniqueId`, ``) === map?.selectedFacilityId,
      )
      const fProps = facilities[0]?.properties
      const formattedAddress = `${fProps?.address}, ${fProps?.city}, ${fProps?.state} ${fProps?.zip}`
      pushDropoffLocationFacilityListingSelection(
        fProps?.uniqueId,
        fProps?.name,
        formattedAddress,
        siteLanguage,
      )
      facilityId = map?.selectedFacilityId
      rerender.current = true
    }
  }, [map?.selectedFacilityId])

  const renderTimeSlots = (day) => (
    <Box>
      {_map(day, (timeSlot) => (
        <Box display="flex" className="segment">
          <Typography variant="body1" className="slot begin">
            {get(timeSlot, `open`, ``)}
          </Typography>
          <Typography variant="body1">-</Typography>
          <Typography variant="body1" className="slot end">
            {get(timeSlot, `close`, ``)}
          </Typography>
        </Box>
      ))}
    </Box>
  )

  const getOpHours = (fProps) => {
    const csrOpHours = get(fProps, `csrOpHours`, ``)
    const otherSchedules = get(csrOpHours, `otherSchedules`, ``)
    const monday = get(csrOpHours, `Monday`, ``)
    const tuesday = get(csrOpHours, `Tuesday`, ``)
    const wednesday = get(csrOpHours, `Wednesday`, ``)
    const thursday = get(csrOpHours, `Thursday`, ``)
    const friday = get(csrOpHours, `Friday`, ``)
    const saturday = get(csrOpHours, `Saturday`, ``)
    const sunday = get(csrOpHours, `Sunday`, ``)
    const hasSeparatedDays =
      monday || tuesday || wednesday || thursday || friday || saturday || sunday

    return hasSeparatedDays || otherSchedules ? (
      <Box
        style={{
          paddingTop: `1.25rem`,
          whiteSpace: `pre-wrap`,
          marginBottom: `1.25rem`,
        }}
      >
        {monday && (
          <Box className={classes.timeSlotWrapper}>
            <Typography variant="body1">{strings.MONDAY}</Typography>
            {renderTimeSlots(monday)}
          </Box>
        )}
        {tuesday && (
          <Box className={classes.timeSlotWrapper}>
            <Typography variant="body1">{strings.TUESDAY}</Typography>
            {renderTimeSlots(tuesday)}
          </Box>
        )}
        {wednesday && (
          <Box className={classes.timeSlotWrapper}>
            <Typography variant="body1">{strings.WEDNESDAY}</Typography>
            {renderTimeSlots(wednesday)}
          </Box>
        )}
        {thursday && (
          <Box className={classes.timeSlotWrapper}>
            <Typography variant="body1">{strings.THURSDAY}</Typography>
            {renderTimeSlots(thursday)}
          </Box>
        )}
        {friday && (
          <Box className={classes.timeSlotWrapper}>
            <Typography variant="body1">{strings.FRIDAY}</Typography>
            {renderTimeSlots(friday)}
          </Box>
        )}
        {saturday && (
          <Box className={classes.timeSlotWrapper}>
            <Typography variant="body1">{strings.SATURDAY}</Typography>
            {renderTimeSlots(saturday)}
          </Box>
        )}
        {sunday && (
          <Box className={classes.timeSlotWrapper}>
            <Typography variant="body1">{strings.SUNDAY}</Typography>
            {renderTimeSlots(sunday)}
          </Box>
        )}
        {otherSchedules && (
          <FormattedTextField>{otherSchedules}</FormattedTextField>
        )}
      </Box>
    ) : null
  }

  const handleCloseFacility = () => {
    onCloseFacility()
  }

  const addDashes = (s) => {
    if (!!s && s.length === 10) {
      return `(${s.slice(0, 3)}) ${s.slice(3, 6)}-${s.slice(6)}`
    }
    return s
  }

  const replaceAddressSpaces = (s) => s.replace(/ /g, `+`)

  const createAddress = (street, city, state, zip) =>
    `${replaceAddressSpaces(street)}+${replaceAddressSpaces(
      city,
    )}+${replaceAddressSpaces(state)}+${replaceAddressSpaces(zip)}`

  // Redirect to get google map directions.
  const redirectGoogle = (fromAddress, toAddress) => {
    const url = `https://www.google.com/maps/dir/${fromAddress}/${toAddress}`
    ssrWindow.open(url, `_blank`)
  }

  const renderPhoneNumbers = (fProps) => {
    const csrPhone =
      fProps.csrPhone !== `9999999999` && addDashes(fProps.csrPhone)
    const salesPhone =
      fProps.salesPhone !== `9999999999` && addDashes(fProps.salesPhone)

    const csrPhoneContainer = csrPhone && (
      <Box display="flex" flexWrap="wrap">
        <Typography variant="body1">{`${strings.CUSTOMER_SERVICE}:`}</Typography>
        <Typography
          variant="body1"
          style={{ marginLeft: `.25rem` }}
          x-ms-format-detection="none"
        >
          {csrPhone}
        </Typography>
      </Box>
    )

    const salesPhoneContainer = salesPhone && (
      <Box display="flex" flexWrap="wrap">
        <Typography variant="body1">{`${strings.SALES}:`}</Typography>
        <Typography
          variant="body1"
          style={{ marginLeft: `.25rem` }}
          x-ms-format-detection="none"
        >
          {salesPhone}
        </Typography>
      </Box>
    )

    return (
      (csrPhone || salesPhone) && (
        <div className="phone d-flex justify-content-between flex-wrap pr-4">
          {csrPhoneContainer}
          {salesPhoneContainer}
        </div>
      )
    )
  }

  const getFProps = (properties) => {
    const csrOpHours = get(properties, `csrOpHours`, ``)
    const materialsAccepted = get(properties, `materialsAccepted`, ``)

    return assign({}, properties, {
      csrOpHours,
      materialsAccepted,
    })
  }

  const renderFullPaneVariant = (id, fProps, facilityAlert) => {
    const {
      facUrl,
      name,
      address,
      city,
      state,
      zip,
      materialsAccepted,
      distance,
    } = fProps
    const hasFacilityAlert = !isEmpty(facilityAlert)
    const alertReason = get(facilityAlert, `description`, ``)

    const holidays = get(fProps, `holidays`, ``)
    const opHours = getOpHours(fProps)
    const today = dayjs().format(`dddd`)
    const todaysHours = get(fProps, `csrOpHours.${today}`, ``)

    return (
      <div
        className={`FacilityDetails ${classes.root}`}
        style={style}
        data-testid="FacilityDetails"
      >
        <Box className={classes.leftOffset} paddingTop="1.5rem">
          <ButtonArrow
            arrowDirection="left"
            shouldNotTrackInComponent
            onClick={handleCloseFacility}
          >
            {strings.LIST}
          </ButtonArrow>
        </Box>
        <Box className={classes.leftOffset}>
          <Box display="flex" overflowY="scroll" mt="1.5rem">
            <Box
              className={classes.leftInnerOffset}
              flexGrow={1}
              width="70%"
              mr=".5rem"
            >
              <Box display="flex" justifyContent="space-between">
                {facUrl ? (
                  <Link
                    shouldNotTrackInComponent
                    className="maax-font-weight-bold"
                    style={{
                      cursor: `pointer`,
                    }}
                    onClick={() => {
                      ssrWindow.location = formatUrl(facUrl)
                    }}
                  >
                    {name}
                  </Link>
                ) : (
                  <Typography variant="subheading2">{name}</Typography>
                )}
                {distance && (
                  <Typography
                    style={{ paddingRight: `.5rem` }}
                    variant="overline"
                  >
                    {parseFloat(distance).toFixed(2)} mi
                  </Typography>
                )}
              </Box>
              <Box className={classes.rightOffset}>
                <Typography>{fProps.address}</Typography>
                <Box display="flex" flexWrap="wrap">
                  <Typography>
                    {`${fProps.city}, ${fProps.state} ${fProps.zip}`}
                  </Typography>
                  <Box mx="0.5rem">|</Box>
                  <Link
                    testid="FacilityDetailsLink"
                    shouldNotTrackInComponent
                    className="maax-font-weight-regular pl-1"
                    style={{ cursor: `pointer` }}
                    onClick={() => {
                      const customerAddress = map.selectedGeosuggestion.label // map.customerAddress
                      const fromAddress = replaceAddressSpaces(customerAddress)
                      const toAddress = createAddress(address, city, state, zip)
                      redirectGoogle(fromAddress, toAddress)
                    }}
                  >
                    {strings.GET_DIRECTIONS}
                  </Link>
                </Box>
                {renderPhoneNumbers(fProps)}
              </Box>
              {(todaysHours || !isEmpty(holidays)) && (
                <Box className={classes.rightOffset} mt="1.5rem">
                  <Box display="flex">
                    {hasFacilityAlert && alertReason ? (
                      <Box display="flex" flexDirection="column" width="100%">
                        <Box className={classes.alertBox}>
                          <ReactSVG
                            wrapper="span"
                            className={classes.alertIcon}
                            src={getContentUrl(alertIconSvg)}
                          />
                          <FormattedTextField style={{ marginLeft: `.25rem` }}>
                            {alertReason}
                          </FormattedTextField>
                        </Box>
                      </Box>
                    ) : (
                      todaysHours && (
                        <Box display="flex" className={classes.timeSlotWrapper}>
                          <Typography variant="subheading2">Open</Typography>
                          <Typography style={{ marginLeft: `.25rem` }}>
                            {renderTimeSlots(todaysHours)}
                          </Typography>
                        </Box>
                      )
                    )}
                  </Box>
                  <Button
                    overrideClass={`w-100 ${classes.hoursAndHolidays}`}
                    onClick={() => setIsHoursOpen(!isHoursOpen)}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      className={classes.expandButton}
                    >
                      <Typography variant="subheading2">
                        {`${strings.HOURS} & ${strings.HOLIDAYS}`}
                      </Typography>
                      <ReactSVG
                        wrapper="span"
                        src={getContentUrl(
                          `/content/dam/wm/icons/${
                            isHoursOpen ? `minus` : `plus`
                          }-green.svg`,
                        )}
                        className="react-svg"
                      />
                    </Box>
                  </Button>
                  <Box
                    className={`${classes.leftOffset} ${classes.bottomBorder}`}
                    style={{
                      maxHeight: isHoursOpen ? `100rem` : `0`,
                      overflow: `hidden`,
                      transition: `max-height .5s ease`,
                    }}
                  >
                    {opHours}
                    {!isEmpty(holidays) && (
                      <Typography
                        variant="body1"
                        className={opHours ? classes.topBorder : ``}
                        style={{
                          paddingTop: opHours ? `1.25rem` : `0`,
                          marginBottom: `1.25rem`,
                          whiteSpace: `pre-wrap`,
                        }}
                      >
                        <ul>
                          {holidays?.map((holiday, index) => (
                            <li key={index}>{holiday}</li>
                          ))}
                        </ul>
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              {materialsAccepted && (
                <Box className={classes.rightOffset} mt="1.25rem">
                  <Typography variant="subheading2">
                    {strings.ACCEPTED_MATERIALS}
                  </Typography>
                  <ul>
                    {_map(materialsAccepted, (mat, key) => (
                      <li key={key}>
                        <Typography variant="body1">
                          {key === `nonHazardous`
                            ? strings.NONHAZARDOUS
                            : strings.HAZARDOUS}
                        </Typography>
                        <FormattedTextField
                          variant="body1"
                          style={{ marginBottom: `1.25rem` }}
                        >
                          {mat}
                        </FormattedTextField>
                      </li>
                    ))}
                  </ul>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </div>
    )
  }
  if (map.selectedFacilityId !== null) {
    const id = map.selectedFacilityId
    const facilities = get(map, `mapLocations`, []).filter(
      (loc) => get(loc, `properties.uniqueId`, ``) === id,
    )
    const facilityAlert = filter(
      facilityAlerts,
      (alert) => get(alert, `facilities.id`, ``) === id,
    )
    if (facilities.length > 0) {
      const fProps = getFProps(facilities[0].properties)
      return renderFullPaneVariant(
        id,
        fProps,
        get(facilityAlert, `[${get(facilityAlert, `length`, ``) - 1}]`, ``),
      )
    }
  }
  return null
}

// Note: React 19 no-longer supports propTypes. Leaving this for reference.
// FacilityDetails.propTypes = {
//   map: PropTypes.object.isRequired,
//   onCloseFacility: PropTypes.func.isRequired,
//   facilityAlerts: PropTypes.object.isRequired,
//   strings: PropTypes.object.isRequired,
//   style: PropTypes.object,
// }

// Note: React 19 no-longer supports defaultProps. Leaving this for reference.
// FacilityDetails.defaultProps = {
//   style: {},
// }
